import React, { useEffect, useState, useCallback, useRef } from 'react';

interface SpotlightOverlayProps {
  /** CSS selector for the target element */
  targetSelector: string;
  /** Whether clicks pass through to the highlighted element */
  allowInteraction?: boolean;
  /** z-index for the overlay (should be above the panel) */
  zIndex?: number;
  children?: React.ReactNode;
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PADDING = 8;
const TRANSITION_MS = 250;

/**
 * Full-screen overlay with a spotlight cutout around a target element.
 * Uses the box-shadow trick: a single div with massive box-shadow
 * creates the darkened overlay while leaving the cutout transparent.
 */
export function SpotlightOverlay({
  targetSelector,
  allowInteraction = false,
  zIndex = 60,
  children,
}: SpotlightOverlayProps) {
  const [rect, setRect] = useState<Rect | null>(null);
  const rafRef = useRef<number>(0);

  const measure = useCallback(() => {
    const el = document.querySelector(targetSelector);
    if (!el) {
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setRect({
      top: r.top - PADDING,
      left: r.left - PADDING,
      width: r.width + PADDING * 2,
      height: r.height + PADDING * 2,
    });
  }, [targetSelector]);

  useEffect(() => {
    measure();

    const onUpdate = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(measure);
    };

    window.addEventListener('scroll', onUpdate, true);
    window.addEventListener('resize', onUpdate);

    // Observe DOM mutations that could move the target
    const observer = new MutationObserver(onUpdate);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });

    return () => {
      window.removeEventListener('scroll', onUpdate, true);
      window.removeEventListener('resize', onUpdate);
      observer.disconnect();
      cancelAnimationFrame(rafRef.current);
    };
  }, [measure]);

  if (!rect) {
    // Target not found — render a full overlay so the tooltip still shows
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex,
          background: 'rgba(0, 0, 0, 0.7)',
        }}
      >
        {children}
      </div>
    );
  }

  return (
    <>
      {/* Spotlight cutout using box-shadow */}
      <div
        style={{
          position: 'fixed',
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          borderRadius: 'var(--sw-border-radius, 8px)',
          boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.7)',
          zIndex,
          pointerEvents: allowInteraction ? 'none' : 'auto',
          transition: `top ${TRANSITION_MS}ms ease, left ${TRANSITION_MS}ms ease, width ${TRANSITION_MS}ms ease, height ${TRANSITION_MS}ms ease`,
        }}
      />
      {/* Click-blocker for the rest of the screen (areas outside the cutout) */}
      {!allowInteraction && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: zIndex - 1,
            // transparent — the box-shadow on the cutout div provides the visual overlay
          }}
        />
      )}
      {/* Children (tooltip) positioned relative to the viewport */}
      {children}
    </>
  );
}

/**
 * Utility: get bounding rect of a target element for tooltip positioning.
 */
export function getTargetRect(selector: string): DOMRect | null {
  const el = document.querySelector(selector);
  return el ? el.getBoundingClientRect() : null;
}
